import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapPin, Loader2, Search, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Nouveau picker de point relais via l'API Sendcloud.
 * Remplace MondialRelayPicker — fonctionne avec Mondial Relay, Chronopost,
 * UPS, DHL, etc. depuis un seul backend.
 *
 * Props:
 *  - postalCode (string)   : code postal du destinataire (réactualise la recherche)
 *  - country (string)      : "FR" par défaut
 *  - selected (object|null): point relais déjà choisi
 *  - onSelect (function)   : appelé avec le point relais retenu
 *  - carrier (string|null) : filtre optionnel (ex: "mondial_relay")
 */
export default function SendcloudServicePointPicker({ postalCode, country = "FR", selected, onSelect, carrier = null }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchedPostal, setSearchedPostal] = useState("");
  const debounceRef = useRef(null);

  const search = async (cp) => {
    if (!cp || cp.length < 3) return;
    setLoading(true);
    setError("");
    try {
      const params = { country, postal_code: cp };
      if (carrier) params.carrier = carrier;
      const res = await axios.get(`${API}/sendcloud/service-points`, { params });
      const list = res.data?.service_points || [];
      setPoints(list);
      setSearchedPostal(cp);
      if (list.length === 0) {
        setError("Aucun point relais trouvé autour de ce code postal.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur de recherche";
      setError(typeof detail === "string" ? detail : "Erreur de recherche");
      setPoints([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-recherche quand le code postal change (debounce 500 ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (postalCode && postalCode.length >= 4 && postalCode !== searchedPostal) {
      debounceRef.current = setTimeout(() => search(postalCode), 500);
    }
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode]);

  const isSelected = (p) => selected && (selected.id === p.id);

  return (
    <div className="space-y-4" data-testid="sendcloud-picker">
      {/* Recherche manuelle */}
      <div className="flex gap-2">
        <Input
          placeholder="Code postal pour chercher (ex: 75001)"
          defaultValue={postalCode || ""}
          onKeyDown={(e) => { if (e.key === "Enter") search(e.target.value); }}
          className="bg-[#1B132B] border-white/10 flex-1"
          data-testid="sendcloud-postal-input"
        />
        <Button
          onClick={() => {
            const input = document.querySelector('[data-testid="sendcloud-postal-input"]');
            if (input) search(input.value);
          }}
          disabled={loading}
          className="gradient-primary"
          data-testid="sendcloud-search-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </div>

      {/* Résultats */}
      {loading && (
        <div className="text-center py-6 text-[#B59ED1] text-sm">
          <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
          Recherche des points relais…
        </div>
      )}

      {error && !loading && (
        <p className="text-sm text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-lg p-3" data-testid="sendcloud-error">{error}</p>
      )}

      {!loading && points.length > 0 && (
        <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1 border-t border-white/10 pt-3">
          <p className="text-xs text-[#B59ED1] mb-2">
            {points.length} point{points.length > 1 ? "s" : ""} relais autour de {searchedPostal} — clique pour choisir
          </p>
          {points.map((p) => {
            const sel = isSelected(p);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  sel
                    ? "bg-[#D926A9]/15 border-[#D926A9] ring-1 ring-[#D926A9]/40"
                    : "bg-[#1B132B] border-white/10 hover:border-[#D926A9]/40 hover:bg-[#1B132B]/80"
                }`}
                data-testid={`sendcloud-point-${p.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${sel ? "bg-[#D926A9] text-white" : "border-2 border-white/20"}`}>
                    {sel ? <Check className="w-3 h-3" /> : <MapPin className="w-3 h-3 text-[#B59ED1]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-[#F7F3FC] truncate">{p.name}</p>
                    <p className="text-xs text-[#B59ED1] mt-0.5">{p.street} {p.house_number}</p>
                    <p className="text-xs text-[#B59ED1]">{p.postal_code} {p.city}</p>
                    {p.distance !== undefined && (
                      <p className="text-[10px] text-[#00E5FF] mt-1">à {Math.round((p.distance || 0))} m</p>
                    )}
                    {Array.isArray(p.carrier) && p.carrier.length > 0 && (
                      <p className="text-[10px] text-[#B59ED1] mt-1">via {p.carrier.join(", ")}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Confirmation point relais choisi */}
      {selected && (
        <div className="rounded-lg border border-[#00E5FF]/30 bg-[#00E5FF]/5 p-3 mt-3" data-testid="sendcloud-selected">
          <div className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#00E5FF] mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-[#F7F3FC]">{selected.name}</p>
              <p className="text-xs text-[#B59ED1]">{selected.street} {selected.house_number}, {selected.postal_code} {selected.city}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
